// TS twin of iOS `InstructionOverlay.swift`.
//
// Two stacked containers (2026-08-21 rev 3):
//   1. message card — the instruction copy
//   2. action pill  — the call to action with a tap glyph
// Separated by a small gap so the CTA reads as its own affordance
// rather than a run-on sentence. Sits BELOW the reticle so the reticle
// stays the visual tap target.
//
// Centering in the `wide` variant is left:0 / right:0 / margin-inline:
// auto with border-box sizing — the earlier left:50% + translateX(-50%)
// plus percentage width let padding push the card off-screen.

import { Pointer } from 'lucide-react'
import { Spinner } from '@/components/spinner/spinner'

interface InstructionOverlayProps {
  /** Plain string or rich node (the aim hint colors "green line"). */
  message: React.ReactNode
  /** Optional CTA rendered as a pill below the message card. */
  action?: React.ReactNode
  isLoading?: boolean
  /** Full-width variant for the aim hint: 90% of the viewport, centered
   *  with symmetric 5% gutters. Default keeps the compact card. */
  wide?: boolean
}

export function InstructionOverlay({
  message,
  action,
  isLoading = false,
  wide = false,
}: InstructionOverlayProps) {
  return (
    <div
      data-testid="instruction-overlay"
      style={{
        position: 'fixed',
        bottom: '20%',
        ...(wide
          ? {
              left: 0,
              right: 0,
              width: '90%',
              marginInline: 'auto',
            }
          : {
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: 'min(80vw, 320px)',
            }),
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        pointerEvents: 'none',
        textAlign: 'center',
      }}
    >
      <div
        data-testid="instruction-message"
        style={{
          boxSizing: 'border-box',
          width: '100%',
          background: 'rgba(0, 0, 0, 0.7)',
          color: 'white',
          borderRadius: 16,
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          fontSize: 15,
          fontWeight: 500,
          lineHeight: 1.35,
        }}
      >
        {isLoading && <Spinner />}
        <span>{message}</span>
      </div>

      {action != null && (
        <div
          data-testid="instruction-action"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(0, 0, 0, 0.82)',
            color: 'white',
            borderRadius: 999,
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: 0.6,
          }}
        >
          <Pointer size={17} strokeWidth={2.25} aria-hidden />
          {action}
        </div>
      )}
    </div>
  )
}
